import { Box } from "@chakra-ui/react";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Card } from "@src/components/ui-kit";
import { observer } from "mobx-react-lite";
import type { OurWorkOverviewModel } from "../../models";
import { useStore } from "../../Store";
import type { ItemTitleProps } from "../ItemTitle";
import WithTotalBudget from "../WithTotalBudget";
import OurWorkHeader from "./OurWorkHeader";
import OurWorkItem from "./OurWorkItem";
import { OurWorkReorderItem } from "./ReorderItem";

type OurWorkItemsProps = {
  structuredItem: OurWorkOverviewModel;
} & Pick<ItemTitleProps, "tabIndex">;

export const OurWorkItems = observer(function OurWorkItems({
  structuredItem,
  tabIndex,
}: OurWorkItemsProps) {
  const store = useStore();
  const sensors = useSensors(useSensor(PointerSensor));
  const visibleBudgetItems = structuredItem.budgetItems.filter(
    (budgetItem) => store.showHiddenOwbi.value || !budgetItem.hidden,
  );
  const visibleBudgetItemIds = visibleBudgetItems.map(({ id }) => id);

  if (!structuredItem.budgetItems.length) {
    return null;
  }

  return (
    <WithTotalBudget totalBudget={structuredItem.total_budget}>
      <Card
        overflowX="scroll"
        borderTopLeftRadius="unset"
        borderColor="gray.300"
      >
        <Box minW="1080px">
          <OurWorkHeader />

          {store.isReorderModeToggled ? (
            <DndContext
              onDragEnd={structuredItem.reorderBudgetItems}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              sensors={sensors}
            >
              <SortableContext
                items={structuredItem.budgetItems}
                strategy={verticalListSortingStrategy}
              >
                {structuredItem.budgetItems.map((budgetItem) => (
                  <OurWorkReorderItem
                    budgetItem={budgetItem}
                    tabIndex={tabIndex}
                    key={budgetItem.id}
                    isProjectTemplate={store.project?.isTemplate}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            visibleBudgetItems.map((bi) => (
              <OurWorkItem
                key={bi.id}
                item={bi}
                tabIndex={tabIndex}
                visibleBudgetItemIds={visibleBudgetItemIds}
              />
            ))
          )}
        </Box>
      </Card>
    </WithTotalBudget>
  );
});
